/**
 * App Initialization Service
 * Determines the initial screen and configuration BEFORE the React app renders
 * This maintains SPA architecture by avoiding post-render navigation
 */

import { logger } from '../utils/logger';
import { runtimeConfig } from './RuntimeConfigService';
import type { RuntimeConfig } from './RuntimeConfigService';
import type { BrandConfig } from '../contexts/BrandContext';
import { documentVerificationService } from './DocumentVerificationService';
import { analyticsService, JourneyEventType } from './AnalyticsService';
import { sessionReportingService } from './SessionReportingService';
import { sessionStateManager } from '../utils/SessionStateManager';
import type { ScreenType } from '../types/screens';
import {
  parseSkipFlowParams,
  storeSkipFlowParams,
  canSkipIntro,
  clearSkipFlowParams,
  getStoredSkipFlowParams,
} from '../utils/skipFlowParams';
import { resolveFlowMode } from '../utils/newTabHandler';
import {
  detectBrandFromHostname,
  detectEnvironmentFromHostname,
  getBrandUrls,
} from '../utils/brand-urls';
import { getDevBrandSlug } from '../contexts/DevBrandContext';
import { getLaunchCredential } from '../utils/launchCredentials';

const appLogger = logger.clone({ labels: { component: 'AppInitialization' }});

export interface AppInitialState {
  initialized: boolean;
  initialScreen: ScreenType;
  config: RuntimeConfig | null;
  sessionId?: string;
  apiKey?: string;
  returnUrl?: string;
  cancelUrl?: string;
  externalUserId?: string;
  error?: string;
  blockingPortalUrl?: string;
  isMobileQR?: boolean;
  isSessionToken?: boolean;
  emblemUpgradeRequired?: boolean;
}
interface BrandConfigResponse {
  brandSlug?: string;
  productName: string;
  companyName: string;
  theme: Record<string, unknown>;
  docsUrl?: string;
  privacyUrl: string;
  termsUrl: string;
  supportUrl: string | null;
}

interface EmblemCallbackState {
  isAssertionSuccess: boolean;
  isEnrollmentComplete: boolean;
}

interface SessionRoutingState {
  shouldRouteToSuccess: boolean;
  sessionStatus?: string;
}

export class AppInitializationService {
  private static getEmblemCallbackState(
    params: URLSearchParams,
  ): EmblemCallbackState {
    const isAssertionSuccess = params.get('emblem_verified') === 'true';
    const isEnrollmentComplete =
      params.get('emblem_enrollment_complete') === 'true';

    return {
      isAssertionSuccess,
      isEnrollmentComplete,
    };
  }

  private static async getSessionRoutingState(
    sessionId: string | null,
  ): Promise<SessionRoutingState> {
    if (!sessionId) {
      return { shouldRouteToSuccess: false };
    }

    const sessionStatus = await sessionReportingService.getSessionStatus(sessionId);
    const shouldRouteToSuccess =
      sessionStatus.status === 'VERIFIED' || sessionStatus.verified;

    if (shouldRouteToSuccess) {
      appLogger.info('Restoring verified session to success screen', {
        labels: {
          sessionId: sessionId.substring(0, 8) + '...',
          sessionStatus: sessionStatus.status || 'unknown',
          pathname: window.location.pathname,
        },
      });
    }

    return {
      shouldRouteToSuccess,
      sessionStatus: sessionStatus.status,
    };
  }

  /**
   * Detects if an error indicates a session has expired
   * Matches error messages from portal-api's session validation
   */
  private static isSessionExpiredError(error: unknown): boolean {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      return message.includes('session has expired') ||
             message.includes('session expired') ||
             message.includes('expired session');
    }
    // Handle string errors
    if (typeof error === 'string') {
      const message = error.toLowerCase();
      return message.includes('session has expired') ||
             message.includes('session expired') ||
             message.includes('expired session');
    }
    return false;
  }

  /**
   * Main entry point - determines initial app state based on URL parameters and existing session
   */
  static async determineInitialState(): Promise<AppInitialState> {
    const urlParams = new URLSearchParams(window.location.search);
    const parsedFlags = parseSkipFlowParams();
    const storedFlags = getStoredSkipFlowParams();
    const hasSkipIntroParam = urlParams.has('skip_intro');
    const hasAutoReturnParam = urlParams.has('auto_return');
    const skipIntro = hasSkipIntroParam
      ? parsedFlags.skipIntro
      : storedFlags?.skipIntro ?? parsedFlags.skipIntro;
    const autoReturn = hasAutoReturnParam
      ? parsedFlags.autoReturn
      : storedFlags?.autoReturn ?? parsedFlags.autoReturn;
    const emblemUpgradeRequired = urlParams.get('emblem_upgrade') === 'required';
    const emblemResumeReason = urlParams.get('emblem_resume');
    const blockedCode = urlParams.get('blocked');
    // Launch credentials are read as the launch URL supplied them, including
    // after initialization removed them from the address bar.
    const launchSessionToken = getLaunchCredential('sessionToken');
    const launchHandoffToken = getLaunchCredential('handoffToken');

    if (blockedCode) {
      const normalizedCode = blockedCode.toUpperCase();
      const blockedErrorMap: Record<string, string> = {
        SUBSCRIPTION_REQUIRED: 'subscription-required',
        PLAN_LIMIT_REACHED: 'plan-limit-reached',
        SANDBOX_LIMIT_REACHED: 'sandbox-limit-reached',
      };
      const errorType = blockedErrorMap[normalizedCode];

      if (errorType) {
        const brand = await this.resolveBrandForBlockedState();
        return {
          initialized: true,
          initialScreen: 'main' as ScreenType,
          config: brand ? this.buildRuntimeConfigFromBrand(brand) : null,
          error: errorType,
          blockingPortalUrl: getBrandUrls(this.detectEnvironment()).portalUrl,
        };
      }
    }
    appLogger.info('Determining initial app state', {
      labels: {
        hasSessionId: `${urlParams.has('sessionId')}`,
        hasState: `${urlParams.has('state')}`,
        hasSessionToken: `${launchSessionToken !== null}`,
        hasHandoffToken: `${launchHandoffToken !== null}`,
        hasQR: `${urlParams.get('qr') === 'true'}`,
        skipIntro: `${skipIntro}`,
        autoReturn: `${autoReturn}`,
      }
    });

    // Persist flags for later screens (SPA navigation can drop search params)
    storeSkipFlowParams({ skipIntro, autoReturn });
    // Single source of truth for flow-mode classification; also stores it.
    // Order: explicit ?mode= → previously stored mode → window.opener →
    //        default redirect. Browser-supplied destinations are not trusted.
    resolveFlowMode();

    // Check for QR mobile flow (highest priority) via handoffToken
    if (urlParams.get('sessionId') && launchHandoffToken) {
      return await this.initializeMobileQRFlow(urlParams, launchHandoffToken);
    }

    const hasSessionToken = launchSessionToken !== null;

    // The server-issued token is the only supported SDK/API launch authority.
    // Older SDKs may also append state; it is intentionally ignored.
    if (hasSessionToken) {
      return await this.initializeSessionTokenFlow(urlParams);
    }

    // Check for existing session state (page refresh scenario)
    const existingSession = sessionStateManager.loadState();
    if (existingSession && existingSession.sessionId) {
      appLogger.info('Found existing session state, resuming session', {
        labels: {
          sessionId: existingSession.sessionId.substring(0, 8) + '...',
          currentScreen: existingSession.screenFlow.currentScreen,
          progress: `${existingSession.screenFlow.progress}`,
        }
      });

      try {
        const config = await runtimeConfig.loadConfig();
        if (!config.apiKey || !config.sessionToken || config.sessionId !== existingSession.sessionId) {
          throw new Error('Restored session did not resolve to an authenticated configuration');
        }

        documentVerificationService.setApiKey(config.apiKey);
        analyticsService.initialize(existingSession.sessionId, config.apiKey);

        const sessionRoutingState = await this.getSessionRoutingState(existingSession.sessionId);
        const resolvedEmblemUpgradeRequired = emblemUpgradeRequired
          ? true
          : emblemResumeReason
          ? false
          : existingSession.config.emblemUpgradeRequired ?? false;

        // Persisted navigation is convenience state only. A terminal success
        // screen must be re-established from the server-owned session status.
        const restoredScreen = existingSession.screenFlow.currentScreen as ScreenType;
        let initialScreen: ScreenType =
          restoredScreen === 'success' || restoredScreen === 'auto-return'
            ? ('main' as ScreenType)
            : restoredScreen;
        if (sessionRoutingState.shouldRouteToSuccess) {
          initialScreen = 'success' as ScreenType;
        } else if (emblemUpgradeRequired) {
        appLogger.info('Emblem step-up callback detected during session resume', {
          labels: {
            sessionId: existingSession.sessionId.substring(0, 8) + '...',
          },
        });
        initialScreen = 'emblem-step-up' as ScreenType;
        this.clearSkipIntroForEmblemCallbackOverride();
        } else if (emblemResumeReason) {
        appLogger.info('Emblem resume callback detected during session resume', {
          labels: {
            sessionId: existingSession.sessionId.substring(0, 8) + '...',
            resumeReason: emblemResumeReason,
          },
        });
        initialScreen = 'main' as ScreenType;
        this.clearSkipIntroForEmblemCallbackOverride();
        } else if (skipIntro) {
        const can = canSkipIntro();
        if (can.ok) {
          appLogger.info('skip_intro enabled with existing session; starting at camera-access');
          initialScreen = 'camera-access' as ScreenType;
          // Optional: track adoption
          try {
            analyticsService.trackEvent(JourneyEventType.VERIFICATION_FLOW_STARTED, {
              source: 'skip_intro',
              flow: 'existing_session',
            });
          } catch {
            // Analytics failures are non-critical
          }
          clearSkipFlowParams(['skipIntro']);
        } else {
          appLogger.warn('skip_intro requested but validation failed for existing session', { labels: { validation: JSON.stringify(can) }});
          try {
            analyticsService.trackError('session', {
              reason: can.reason || 'skip_intro_validation_failed',
              flow: 'existing_session',
            });
          } catch {
            // Analytics failures are non-critical
          }
        }
        }

        return {
          initialized: true,
          initialScreen,
          config,
          sessionId: config.sessionId,
          apiKey: config.apiKey,
          returnUrl: config.returnUrl,
          cancelUrl: config.cancelUrl,
          externalUserId: config.externalUserId,
          emblemUpgradeRequired: resolvedEmblemUpgradeRequired,
          isMobileQR: existingSession.config.isMobileQR ?? false,
        };
      } catch (error) {
        appLogger.warn('Failed to revalidate restored session', {
          labels: {
            error: error instanceof Error ? error.message : String(error),
          },
        });
        return {
          initialized: true,
          initialScreen: 'main' as ScreenType,
          config: null,
          error: 'Verification Unavailable',
        };
      }
    }

    if (urlParams.has('state')) {
      appLogger.warn('Rejected state-only SDK launch without server-issued session authentication');
      return {
        initialized: true,
        initialScreen: 'main' as ScreenType,
        config: null,
        error: 'Verification Unavailable',
      };
    }

    // Direct access not allowed
    // Direct access not allowed; if skip_intro was requested, degrade gracefully to intro without error screen
    if (skipIntro) {
      appLogger.warn('skip_intro requested but no valid session present; showing intro');
      try {
        analyticsService.trackError('session', {
          reason: 'missing_session_token',
          flow: 'no_session',
        });
      } catch {
        // Analytics failures are non-critical
      }
      return {
        initialized: true,
        initialScreen: 'main' as ScreenType,
        config: null,
      };
    }

    appLogger.warn('Direct access attempted without proper parameters and no existing session');
    // Resolve brand from hostname for correct theming on error screen
    // Brand isolation: must always show the correct brand, never fall back to another brand
    const brand = await this.resolveBrandForBlockedState();
    return {
      initialized: true,
      initialScreen: 'main' as ScreenType,
      config: brand ? this.buildRuntimeConfigFromBrand(brand) : null,
      error: 'Direct Access Not Allowed',
    };
  }

  /**
   * Initialize mobile QR flow (handoffToken + /qr-config → sessionToken)
   * Goes directly to camera-access with runtime config loaded from Portal.
   */
  private static async initializeMobileQRFlow(
    params: URLSearchParams,
    handoffToken: string,
  ): Promise<AppInitialState> {
    const sessionId = params.get('sessionId')!;
    appLogger.info('Initializing mobile QR flow (handoffToken)', {
      labels: {
        sessionId: sessionId.substring(0, 8) + '...',
        hasHandoffToken: `${!!handoffToken}`,
      }
    });

    try {
      // RuntimeConfigService detects handoffToken in URL and fetches /qr-config
      const config = await runtimeConfig.loadConfig();

      if (!config.sessionToken) {
        appLogger.error('QR flow failed to obtain sessionToken from /qr-config');
        return {
          initialized: true,
          initialScreen: 'main' as ScreenType,
          config: null,
          error: 'Failed to initialize mobile session (token)',
        };
      }

      // Initialize downstream services if apiKey present
      if (config.apiKey) {
        documentVerificationService.setApiKey(config.apiKey);
        analyticsService.initialize(sessionId, config.apiKey);
      } else {
        appLogger.warn('QR flow config missing apiKey (merchantId)');
      }

      return {
        initialized: true,
        initialScreen: 'camera-access' as ScreenType,
        config,
        sessionId,
        apiKey: config.apiKey,
        returnUrl: config.returnUrl,
        cancelUrl: config.cancelUrl,
        externalUserId: config.externalUserId,
        isMobileQR: true,
      };
    } catch (error) {
      appLogger.error('Failed to initialize mobile QR flow', error instanceof Error ? error : new Error(`${error}`));

      // Check if this is a session expiry error
      if (this.isSessionExpiredError(error)) {
        return {
          initialized: true,
          initialScreen: 'main' as ScreenType,
          config: null,
          error: 'Session Expired',
        };
      }

      return {
        initialized: true,
        initialScreen: 'main' as ScreenType,
        config: null,
        error: 'Failed to initialize mobile session',
      };
    }
  }

  /**
   * Initialize sessionToken flow (API partners)
   */
  private static async initializeSessionTokenFlow(params: URLSearchParams): Promise<AppInitialState> {
    const sessionId = params.get('sessionId');
    const emblemUpgradeRequired = params.get('emblem_upgrade') === 'required';
    const emblemResumeReason = params.get('emblem_resume');
    const emblemCallbackState = this.getEmblemCallbackState(params);
    
    appLogger.info('Initializing API partner flow with sessionToken');

    try {
      // Load config via sessionToken
      await runtimeConfig.loadConfig();
      const config = runtimeConfig.getConfig();
      
      if (!config.apiKey) {
        throw new Error('Failed to load API key from session configuration');
      }

      // Initialize services
      documentVerificationService.setApiKey(config.apiKey);
      if (sessionId) {
        analyticsService.initialize(sessionId, config.apiKey);
      }

      const sessionRoutingState = await this.getSessionRoutingState(
        sessionId || config.sessionId || null,
      );

      // API partner flow starts at main screen (honor skip_intro if valid)
      const skipIntro = params.get('skip_intro') === 'true';
      const initialScreen = sessionRoutingState.shouldRouteToSuccess
        ? ('success' as ScreenType)
        : emblemUpgradeRequired
          ? ('emblem-step-up' as ScreenType)
          : ('main' as ScreenType);

      if (sessionRoutingState.shouldRouteToSuccess) {
        appLogger.info('Routing sessionToken flow to success', {
          labels: {
            sessionId: sessionId || config.sessionId || 'unknown',
            emblemVerified: `${emblemCallbackState.isAssertionSuccess}`,
            emblemEnrollmentComplete: `${emblemCallbackState.isEnrollmentComplete}`,
            sessionStatus: sessionRoutingState.sessionStatus || 'unknown',
            pathname: window.location.pathname,
            route: initialScreen,
          },
        });
      }

      const base: AppInitialState = {
        initialized: true,
        initialScreen,
        config,
        sessionId: sessionId || config.sessionId,
        apiKey: config.apiKey,
        returnUrl: config.returnUrl,
        cancelUrl: config.cancelUrl,
        externalUserId: config.externalUserId,
        isSessionToken: true,
        emblemUpgradeRequired,
      };

      if (
        sessionRoutingState.shouldRouteToSuccess ||
        emblemUpgradeRequired ||
        emblemResumeReason
      ) {
        this.clearSkipIntroForEmblemCallbackOverride();
      }

      if (
        !sessionRoutingState.shouldRouteToSuccess &&
        !emblemUpgradeRequired &&
        !emblemResumeReason &&
        skipIntro &&
        config.sessionToken
      ) {
        appLogger.info('skip_intro enabled for sessionToken flow; starting at camera-access');
        try {
          analyticsService.trackEvent(JourneyEventType.VERIFICATION_FLOW_STARTED, {
            source: 'skip_intro',
            flow: 'session_token',
          });
        } catch {
          // Analytics failures are non-critical
        }
        base.initialScreen = 'camera-access' as ScreenType;
        clearSkipFlowParams(['skipIntro']);
      }

      return base;
    } catch (error) {
      appLogger.error('Failed to initialize sessionToken flow', error instanceof Error ? error : new Error(`${error}`));

      // Check if this is a session expiry error
      if (this.isSessionExpiredError(error)) {
        return {
          initialized: true,
          initialScreen: 'main' as ScreenType,
          config: null,
          error: 'Session Expired',
        };
      }

      return {
        initialized: true,
        initialScreen: 'main' as ScreenType,
        config: null,
        // A bearer-bearing launch reached the authenticated initialization
        // path, so a configuration failure is an availability problem rather
        // than prohibited direct access.
        error: 'Verification Unavailable',
      };
    }
  }

  private static clearSkipIntroForEmblemCallbackOverride(): void {
    clearSkipFlowParams(['skipIntro']);

    const url = new URL(window.location.href);
    if (!url.searchParams.has('skip_intro')) {
      return;
    }

    url.searchParams.delete('skip_intro');
    window.history.replaceState({}, '', url.toString());
  }

  private static async resolveBrandForBlockedState(): Promise<BrandConfig | null> {
    try {
      const environment = this.detectEnvironment();
      const { apiUrl } = getBrandUrls(environment);
      const brandConfig = await this.fetchBrandConfig(apiUrl);
      return brandConfig ? this.toBrandConfig(brandConfig) : null;
    } catch (error) {
      appLogger.warn('Failed to resolve brand config for blocked flow', {
        labels: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
      return null;
    }
  }

  private static async fetchBrandConfig(apiUrl: string): Promise<BrandConfigResponse | null> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const devBrand = getDevBrandSlug();
    if (devBrand) {
      headers['X-Brand-Override'] = devBrand;
    }

    const response = await fetch(`${apiUrl}/api/v1/brand-config`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      appLogger.warn('Brand config request failed', {
        labels: {
          status: String(response.status),
          statusText: response.statusText,
        },
      });
      return null;
    }

    return (await response.json()) as BrandConfigResponse;
  }

  private static toBrandConfig(raw: BrandConfigResponse): BrandConfig | null {
    if (!raw || !raw.theme || typeof raw.theme !== 'object') {
      return null;
    }

    const theme = raw.theme as unknown as BrandConfig['theme'];
    const themeLogoUrl = (raw.theme as { logoUrl?: string }).logoUrl;
    const logoUrl: string =
      typeof themeLogoUrl === 'string' ? themeLogoUrl : '';

    return {
      brandSlug: raw.brandSlug,
      productName: raw.productName,
      companyName: raw.companyName,
      theme,
      logoUrl,
      docsUrl: raw.docsUrl,
      supportUrl: raw.supportUrl,
      privacyUrl: raw.privacyUrl,
      termsUrl: raw.termsUrl,
    };
  }

  private static buildRuntimeConfigFromBrand(brand: BrandConfig): RuntimeConfig {
    const environment = this.detectEnvironment();
    const urls = getBrandUrls(environment);
    const expectedBrand = detectBrandFromHostname(window.location.hostname);
    if (expectedBrand && brand.brandSlug !== expectedBrand) {
      throw new Error('Brand configuration does not match the trusted hostname');
    }

    return {
      apiUrl: urls.apiUrl,
      engineUrl: urls.engineUrl,
      wsUrl: urls.wsUrl,
      internalServiceToken: '',
      environment,
      features: {
        testMode: environment === 'development',
        qualityThreshold: 0.55,
        sandboxMode: false,
      },
      brand,
    };
  }

  private static detectEnvironment(): 'development' | 'staging' | 'production' {
    if (typeof window === 'undefined') {
      return 'development';
    }

    return detectEnvironmentFromHostname(window.location.hostname);
  }

  // Note: getApiUrl removed — mobile QR flow now uses handoffToken with
  // RuntimeConfigService to fetch /qr-config and obtain sessionToken.
}
